import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'

interface Feature {
  name: string
  enabled: boolean
}

interface FeaturesResponse {
  environment: string
  features: Feature[]
}

interface FeatureFlagsContextValue {
  isEnabled: (name: string) => boolean
  isLoading: boolean
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  isEnabled: () => false,
  isLoading: false,
})

export function FeatureFlagsProvider({ children }: PropsWithChildren<{}>) {
  const [flags, setFlags] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()

    fetch('/features', { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`/features returned ${r.status}`)
        return r.json()
      })
      .then((data: FeaturesResponse) => {
        setFlags(Object.fromEntries(data.features.map((f) => [f.name, f.enabled])))
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          // Fail closed — all flags disabled if the endpoint is unreachable
          // eslint-disable-next-line no-console
          console.error('[FeatureFlags] Failed to load feature flags:', err)
        }
      })
      .finally(() => setIsLoading(false))

    return () => controller.abort()
  }, [])

  return (
    <FeatureFlagsContext.Provider value={{ isEnabled: (name) => flags[name] ?? false, isLoading }}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

export function useFeatureFlagsContext() {
  return useContext(FeatureFlagsContext)
}
